import { useReportStore } from '../../../stores/useReportStore';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import Section from '../widgets/Section';
import StatusPill from '../widgets/StatusPill';
import { toneFor } from '../widgets/tone';

function safeArray<T>(val: unknown, ...keys: string[]): T[] {
  if (Array.isArray(val)) return val as T[];
  if (val && typeof val === 'object') {
    for (const key of keys) {
      const nested = (val as Record<string, unknown>)[key];
      if (Array.isArray(nested)) return nested as T[];
    }
  }
  return [];
}

/**
 * Pretty-print a finding row's text. The backend often joins `item — detail`
 * into one string; render the lead phrase emphasised and the detail muted
 * so the cards stay scannable at a glance.
 */
function FindingRow({ text, status }: { text: string; status: string }) {
  const sepIdx = text.indexOf(' — ');
  const head = sepIdx > 0 ? text.slice(0, sepIdx) : text;
  const detail = sepIdx > 0 ? text.slice(sepIdx + 3) : '';
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <div className="min-w-0 flex-1">
        <div className="font-medium leading-tight">{head}</div>
        {detail && (
          <div className="mt-0.5 text-xs leading-snug text-muted-foreground">{detail}</div>
        )}
      </div>
      <StatusPill tone={toneFor(status)} className="shrink-0">{status}</StatusPill>
    </div>
  );
}

export default function SeoAeo() {
  const data = useReportStore((s) => s.reportData);
  if (!data) return null;

  const seo = (data.seo || {}) as unknown as Record<string, unknown>;
  const aeo = (data.aeo || {}) as unknown as Record<string, unknown>;

  const seoDomain = String(seo.domain || aeo.domain || '');
  const aeoSummary = String(aeo.summary || '');

  const scores = safeArray<Record<string, unknown>>(seo, 'scores');
  const technical = safeArray<Record<string, unknown>>(seo, 'technical');
  const content = safeArray<Record<string, unknown>>(seo, 'content');
  const competitive = safeArray<Record<string, unknown>>(seo, 'competitive');
  const aeoItems = safeArray<Record<string, unknown>>(aeo, 'items');
  const aeoOverall = (aeo.overallScore ?? aeo.overall_score ?? aeo.readiness_score ?? 0) as number;

  const hasSeoData = scores.length > 0 || technical.length > 0 || content.length > 0 || competitive.length > 0;
  const hasAeoData = aeoItems.length > 0 || aeoOverall > 0;
  const hasAnything = hasSeoData || hasAeoData;

  // Compose a domain link: prefer the analysed domain, fall back to a
  // best-effort prefix when the value isn't already a full URL.
  const domainHref = seoDomain
    ? (seoDomain.startsWith('http') ? seoDomain : `https://${seoDomain}`)
    : '';

  if (!hasAnything) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>SEO &amp; AEO</CardTitle>
          <CardDescription>
            Connect a website URL on the Project Info step to unlock SEO and Answer-Engine
            Optimization scorecards. Without a URL the audit can't fetch the homepage to
            score technical SEO, structured data, FAQ coverage, or AI-search readiness.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div>
      {/* Header: domain + at-a-glance scores */}
      {(seoDomain || aeoOverall > 0) && (
        <Card className="mb-6.5">
          <CardHeader className="flex flex-row items-start justify-between gap-3">
            <div className="min-w-0">
              <CardTitle>SEO &amp; AEO Audit</CardTitle>
              <CardDescription className="mt-1">
                Heuristic scorecard for{' '}
                {seoDomain ? (
                  <a
                    href={domainHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-foreground underline decoration-dotted underline-offset-2 hover:decoration-solid"
                  >
                    {seoDomain}
                  </a>
                ) : 'this site'}{' '}
                — homepage analysis only; pair with Lighthouse &amp; Search Console for
                deeper diagnostics.
              </CardDescription>
            </div>
            {aeoOverall > 0 && (
              <div className="shrink-0 text-right">
                <div className="text-3xl font-bold leading-none">{aeoOverall}<span className="text-base font-normal text-muted-foreground">/100</span></div>
                <div className="mt-1 text-xs uppercase tracking-wide text-muted-foreground">AEO Readiness</div>
              </div>
            )}
          </CardHeader>
        </Card>
      )}

      {/* SEO Scorecard */}
      <Section
        title="SEO scorecard"
        hint="Domain authority, organic traffic, keyword rankings and content velocity against industry benchmarks."
      >
        {scores.length > 0 ? (
          <div className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
            {scores.map((score, i) => (
              <Card size="sm" key={String(score.label || i)}>
                <CardContent className="px-4 py-1">
                  <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                    {String(score.label || '')}
                  </div>
                  <div
                    className="mt-2 text-[26px] font-bold leading-none tracking-[-0.025em] tabular-nums"
                    style={{ color: String(score.color || 'var(--foreground)') }}
                  >
                    {String(score.value || 'Unknown')}
                  </div>
                  <div className="mt-1.5 min-h-4 text-[11.5px] text-muted-foreground">
                    {score.benchmark ? `Benchmark: ${score.benchmark}` : ''}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Scorecard data not available — provide GA4 organic traffic and Domain
            Authority (via Moz / Ahrefs / SEMrush) to populate this card.
          </p>
        )}
      </Section>

      {/* SEO Findings (Technical + Content side-by-side) */}
      {(technical.length > 0 || content.length > 0) && (
        <Section title="SEO findings" hint="Per-area checklist with status and remediation hint.">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {technical.length > 0 && (
                <Card size="sm">
                  <CardContent className="space-y-3 px-5 py-1">
                    <h3 className="text-sm font-bold tracking-[-0.01em] text-foreground">Technical SEO</h3>
                    {technical.map((item, i) => (
                      <FindingRow
                        key={i}
                        text={String(item.text || item.item || '')}
                        status={String(item.status || '')}
                      />
                    ))}
                  </CardContent>
                </Card>
              )}
              {content.length > 0 && (
                <Card size="sm">
                  <CardContent className="space-y-3 px-5 py-1">
                    <h3 className="text-sm font-bold tracking-[-0.01em] text-foreground">Content SEO</h3>
                    {content.map((item, i) => (
                      <FindingRow
                        key={i}
                        text={String(item.text || item.item || '')}
                        status={String(item.status || '')}
                      />
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
        </Section>
      )}

      {/* Competitive Position */}
      {competitive.length > 0 && (
        <Section
          title="Competitive SEO position"
          hint="Keyword rankings, brand search, review profiles and backlink strength."
        >
          <Card>
            <CardContent className="space-y-3 px-5 py-1">
              {competitive.map((item, i) => (
                <FindingRow
                  key={i}
                  text={String(item.text || item.item || '')}
                  status={String(item.status || '')}
                />
              ))}
            </CardContent>
          </Card>
        </Section>
      )}

      {/* AEO Readiness */}
      {(aeoItems.length > 0 || aeoSummary) && (
        <Section
          title="AEO readiness"
          hint="Six-dimension readiness assessment for Answer Engines (ChatGPT, Perplexity, Google AI Overviews) — how well the site is structured for citation."
        >
          <div className="space-y-3">
            {aeoSummary && (
              <p className="max-w-[78ch] text-sm leading-relaxed text-muted-foreground text-pretty">{aeoSummary}</p>
            )}
            {aeoItems.length > 0 && (
              <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
                {aeoItems.map((item, i) => (
                  <Card size="sm" key={String(item.title || i)}>
                    <CardContent className="space-y-1.5 px-4.5 py-1">
                      <div className="flex items-center justify-between gap-2.5">
                        <span className="text-sm font-semibold text-foreground">{String(item.title || '')}</span>
                        <span
                          className="font-mono text-sm font-bold tabular-nums"
                          style={{ color: String(item.color || 'var(--foreground)') }}
                        >
                          {String(item.score || 0)}/100
                        </span>
                      </div>
                      <p className="text-xs leading-relaxed text-muted-foreground text-pretty">
                        {String(item.desc || item.description || '')}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </Section>
      )}
    </div>
  );
}
